import React from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Container,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import CitySearch from '../components/forms/CitySearch';
import { AppMode, AYANAMSHA_OPTIONS, AyanamshaMode, DefaultCity, HOUSE_SYSTEM_OPTIONS, HouseSystemMode, KundliChartStyle, NodeMode, THEME_PALETTE_OPTIONS, ThemePaletteName, useUserPreferences } from '../contexts/UserPreferencesContext';
import { LANGUAGE_OPTIONS, SupportedLanguage, getLanguagePreferenceName, normalizeLanguage } from '../i18n/languages';
import { translateInline } from '../i18n/inlineTranslations';

const Settings: React.FC = () => {
  const {
    chartStyle,
    setChartStyle,
    mode,
    setMode,
    themePalette,
    setThemePalette,
    defaultCity,
    setDefaultCity,
    nodeMode,
    setNodeMode,
    houseSystem,
    setHouseSystem,
    ayanamshaMode,
    setAyanamshaMode,
    useKpForNadiKp,
    setUseKpForNadiKp,
    showOuterPlanets,
    setShowOuterPlanets
  } = useUserPreferences();
  const { i18n } = useTranslation();
  const language = normalizeLanguage(i18n.resolvedLanguage || i18n.language);
  const isHindi = language === 'hi';
  const text = (english: string) => translateInline(language, english);

  const changeLanguage = (nextLanguage: SupportedLanguage) => {
    i18n.changeLanguage(nextLanguage);
    if (localStorage.getItem('nadiAstroToken')) {
      axios.put('/users/profile', {
        preferences: { language: getLanguagePreferenceName(nextLanguage) },
      }).catch(() => {});
    }
  };

  const changeChartStyle = (_event: React.MouseEvent<HTMLElement>, nextStyle: KundliChartStyle | null) => {
    if (nextStyle) setChartStyle(nextStyle);
  };

  const changeMode = (_event: React.MouseEvent<HTMLElement>, nextMode: AppMode | null) => {
    if (nextMode) setMode(nextMode);
  };

  const changeNodeMode = (_event: React.MouseEvent<HTMLElement>, nextNodeMode: NodeMode | null) => {
    if (nextNodeMode) setNodeMode(nextNodeMode);
  };

  const resetDefaults = () => {
    setChartStyle('north');
    setMode('light');
    setThemePalette('green');
    setDefaultCity(null);
    setNodeMode('true');
    setHouseSystem('KP_PLACIDUS');
    setAyanamshaMode('LAHIRI');
    setUseKpForNadiKp(true);
    setShowOuterPlanets(false);
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 3 }, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
        <Typography variant="h1" sx={{ fontSize: 20, mb: 0.5 }}>
          Settings
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          App preferences and calculation defaults for Kundlistar Studio.
        </Typography>

        <SettingSection title="App preferences">
          <SettingRow label="Language">
            <Select fullWidth size="small" value={language} onChange={(event) => changeLanguage(event.target.value as SupportedLanguage)}>
              {LANGUAGE_OPTIONS.map((option) => <MenuItem key={option.code} value={option.code}>{option.label}</MenuItem>)}
            </Select>
          </SettingRow>

          <SettingRow label={text('Mode')}>
            <ToggleButtonGroup exclusive value={mode} onChange={changeMode} fullWidth>
              <ToggleButton value="light">{text('Light')}</ToggleButton>
              <ToggleButton value="dark">{text('Dark')}</ToggleButton>
              <ToggleButton value="night">{text('Night')}</ToggleButton>
            </ToggleButtonGroup>
          </SettingRow>

          <SettingRow label={text('Theme Color')}>
            <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap>
              {THEME_PALETTE_OPTIONS.map((option) => {
                const active = option.value === themePalette;
                return (
                  <Button
                    key={option.value}
                    variant={active ? 'contained' : 'outlined'}
                    onClick={() => setThemePalette(option.value as ThemePaletteName)}
                    sx={{
                      minWidth: 106,
                      justifyContent: 'flex-start',
                      borderColor: option.swatch,
                      bgcolor: active ? option.swatch : 'transparent',
                      color: active ? '#fff' : 'text.primary',
                      '&:hover': {
                        bgcolor: active ? option.swatch : `${option.swatch}12`,
                        borderColor: option.swatch,
                      },
                    }}
                  >
                    <Box component="span" sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: option.swatch, border: active ? '1px solid rgba(255,255,255,0.9)' : '1px solid rgba(0,0,0,0.14)', mr: 1 }} />
                    {isHindi ? option.hindiLabel : text(option.label)}
                  </Button>
                );
              })}
            </Stack>
            <Alert severity="info" sx={{ mt: 1.5 }}>
              {text('This color is saved for the website theme and PDF report accent.')}
            </Alert>
          </SettingRow>
        </SettingSection>

        <SettingSection title="Calculation defaults">
          <SettingRow label="Chart style">
            <ToggleButtonGroup exclusive value={chartStyle} onChange={changeChartStyle} fullWidth>
              <ToggleButton value="north">North</ToggleButton>
              <ToggleButton value="south">South</ToggleButton>
              <ToggleButton value="east">East</ToggleButton>
            </ToggleButtonGroup>
          </SettingRow>

          <SettingRow label={isHindi ? 'हाउस सिस्टम' : 'House system'}>
            <Select
              fullWidth
              size="small"
              value={houseSystem}
              onChange={(event) => setHouseSystem(event.target.value as HouseSystemMode)}
            >
              {HOUSE_SYSTEM_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {isHindi ? option.hindiLabel : option.label}
                </MenuItem>
              ))}
            </Select>
          </SettingRow>

          <SettingRow label={isHindi ? 'राहु/केतु गणना' : 'Rahu/Ketu calculation'}>
            <ToggleButtonGroup exclusive value={nodeMode} onChange={changeNodeMode} fullWidth>
              <ToggleButton value="true">{isHindi ? 'ट्रू नोड' : 'True Node'}</ToggleButton>
              <ToggleButton value="mean">{isHindi ? 'मीन नोड' : 'Mean Node'}</ToggleButton>
            </ToggleButtonGroup>
          </SettingRow>

          <SettingRow label={isHindi ? 'अयनांश' : 'Ayanamsha'}>
            <Select fullWidth size="small" value={ayanamshaMode} onChange={(event) => setAyanamshaMode(event.target.value as AyanamshaMode)}>
              {AYANAMSHA_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {isHindi ? option.hindiLabel : option.label}
                </MenuItem>
              ))}
            </Select>
            <FormControlLabel
              sx={{ mt: 1 }}
              control={<Checkbox checked={useKpForNadiKp} onChange={(event) => setUseKpForNadiKp(event.target.checked)} />}
              label={isHindi ? 'Nadi aur KP ke liye KP ayanamsha use karein' : 'Use KP ayanamsha for Nadi and KP'}
            />
          </SettingRow>

          <SettingRow label={isHindi ? 'बाहरी ग्रह' : 'Outer planets'}>
            <FormControlLabel
              control={<Switch checked={showOuterPlanets} onChange={(event) => setShowOuterPlanets(event.target.checked)} />}
              label={isHindi ? 'Uranus, Neptune और Pluto दिखाएं' : 'Show Uranus, Neptune and Pluto'}
            />
          </SettingRow>

          <SettingRow label={isHindi ? 'डिफॉल्ट शहर / टाइमज़ोन' : 'Default city / timezone'}>
            <CitySearch
              value={defaultCity as any}
              onChange={(city) => setDefaultCity(city as DefaultCity | null)}
              label={isHindi ? 'डिफॉल्ट शहर' : 'Default city'}
              placeholder={isHindi ? 'अपना डिफॉल्ट शहर खोजें' : 'Search your default city'}
            />
            <Alert severity={defaultCity ? 'success' : 'info'} sx={{ mt: 1.5 }}>
              {defaultCity
                ? `${defaultCity.displayName || defaultCity.name} · ${defaultCity.timezone?.name || 'Timezone'}`
                : 'No default city selected. Calculation pages will use New Delhi when a fallback is needed.'}
            </Alert>
            {defaultCity && (
              <Button sx={{ mt: 1 }} color="warning" onClick={() => setDefaultCity(null)}>
                {isHindi ? 'Default city हटाएं' : 'Clear default city'}
              </Button>
            )}
          </SettingRow>
        </SettingSection>

        <Button fullWidth variant="outlined" onClick={resetDefaults}>
          Reset to defaults
        </Button>
      </Paper>
    </Container>
  );
};

function SettingSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 2.5, mb: 3 }}>
      <Typography variant="h2" sx={{ fontSize: 16, mb: 2 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function SettingRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600 }}>
        {label}
      </Typography>
      <Box sx={{ mt: 1 }}>{children}</Box>
    </Box>
  );
}

export default Settings;
